import React, { useState, useEffect, useCallback, useRef } from "react";
import { Field, Form, Formik } from "formik";
import {
  getPostInitialValues,
  VALIDATION_SCHEMA,
} from "utils/requestOrdersUtils";
import moment from "moment";
import { FORMAT } from "utils/dateOfBirthValidation";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AlertModal from "components/AlertModal";
import { stateConvert } from "utils/stateAbbreviation";
import UploadSpecification from "components/UploadSpecification";
import LoadDetails from "pages/request-orders/loadDetails";
import NumberInput from "components/FormElements/NumberInput";
import RadioButton from "components/FormElements/RadioButton";
import MaterialToolTip from "components/MaterialToolTip";
import CheckboxInput from "components/FormElements/CheckboxInput";
import TextInput from "components/FormElements/TextInput";
import Textarea from "components/FormElements/TextArea";
import Select from "components/FormElements/Select";
import Layout from "components/Layout";
import Button from "components/Button";
import TooltipMaterial from "@material-ui/core/Tooltip";
import { UploadSpec } from "styles/pages/request-orders";
import UploadDummy from "assets/images/UploadDummy.png";
import { TopHeading } from "assets/css/global";
import HeadingIcon from "assets/svg/RequestOrderIcon";
import { BodyContainer, Footer } from "styles/pages/global";
import {
  uploadSpectFile,
  createOrder,
  updateOrder,
  orderRefNumber,
  locationList,
  palletDetails,
} from "redux/actions/requestOrders";
import { FormWrapper } from "styles/pages/request-orders";
import { useText } from "locales/useGetText";

import MixPalletRequestOrder from "./mixPalletRequestOrder";
import { AddMixPalletBtn, HardSoftWoodWrapper, OuterWrapper } from "./OrderStyled";
import {
  equipmentList,
  hardOrSoftWoodArray,
  woodConditions,
  plasticConditions,
  materialArr,
  palletList,
  comboPalletList,
  corePalletList,
} from "./constants";

import { getLoggedInUserCountry } from "helpers";

const RequestOrders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locale = useText("requestOrder");
  const loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserData") || "{}");

  const userCountry = getLoggedInUserCountry();
  const isUSA = userCountry?.toUpperCase() === "USA";

  // Mode & Edit State
  const [isModifyForm, setIsModifyForm] = useState(false);
  const [modifiedOrderRefNo, setModifiedOrderRefNo] = useState();
  const [isMixLoadMode, setIsMixLoadMode] = useState(false);
  const [cloneOrderFlag, setCloneOrderFlag] = useState(false);

  // Configuration & Location Data State
  const [palletDetailResponse, setPalletDetailResponse] = useState();
  const [locationListResponse, setLocationListResponse] = useState([]);
  const [addresResponse, setAddresResponse] = useState();
  const [editResponse, setEditResponse] = useState();

  // Selected Options & Spec Cascading State
  const [materialResponse, setMaterialResponse] = useState("WOOD");
  const [hardOrSoftWoodValue, setHardOrSoftWoodValue] = useState("");
  const [getMoisture, setMoisture] = useState([]);
  const [getSizeList, setSizeList] = useState([]);
  const [getGradeList, setGradeList] = useState([]);
  const [quantityValueState, setQuantityValueState] = useState(540);
  const [maxQuantityState, setMaxQuantityState] = useState(540);
  const [isHeatTrearedFlag, setIsHeatTreared] = useState(false);
  const [loadDetailsState, setLoadDetailsState] = useState();

  // Upload & File State
  const [pendingFiles, setPendingFiles] = useState();
  const [uploadImage, setUploadImage] = useState();
  const [productPostImage, setDefaultPostImage] = useState();
  const [uploadSpectState, setUploadSpectState] = useState();

  // Modal State
  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("Alert");
  const [modalLink, setModalLink] = useState("/");
  const [successCreateOrder, setSuccessCreateOrder] = useState(false);

  const toggleModal = () => setModal(prev => !prev);

  // 1. Initial Data Fetching
  useEffect(() => {
    fetchPalletDetailsConfig();
    fetchUserLocations();

    const ordRefNo = localStorage.getItem("ordRefNoForFetchReqOrd");
    if (ordRefNo) {
      setIsModifyForm(true);
      setModifiedOrderRefNo(ordRefNo);
      localStorage.removeItem("ordRefNoForFetchReqOrd");
    }
  }, []);

  const fetchPalletDetailsConfig = useCallback(async () => {
    let response = await dispatch(palletDetails(loggedInUserInfo?.token));
    if (response?.data?.success) {
      setPalletDetailResponse(response?.data?.data);
    } else {
      if (response?.data?.error?.message === "Invalid Token") {
        navigate("/");
      } else {
        setModalContent(response?.data?.error?.message || response?.message || locale('genericErrorLater'));
        setModalLink("");
        toggleModal();
      }
    }
  }, [dispatch, loggedInUserInfo?.token, navigate, locale]);

  const fetchUserLocations = useCallback(async () => {
    if (!loggedInUserInfo?.token) {
      setModalContent(locale('pleaseSignInAgain'));
      setModalLink("/");
      toggleModal();
      return;
    }
    let response = await dispatch(
      locationList(
        loggedInUserInfo?.locationType,
        loggedInUserInfo?.customerId,
        loggedInUserInfo?.token
      )
    );

    if (response?.data?.success) {
      let arr = [];
      let locations = response?.data?.data?.locations || [];
      for (let i = 0; i < locations.length; i++) {
        let mainLoc = locations[i].mainLocation;
        if (mainLoc) {
          let addr = mainLoc.address;
          let stateVal = stateConvert(addr?.state) || addr?.state;
          let labelSet = `${mainLoc.locationName}, ${addr?.street}, ${addr?.city}, ${stateVal}, ${addr?.zipCode}`;
          let addressObj = {
            city: addr?.city,
            contactNumber: addr?.contactNumber,
            locationName: addr?.locationName,
            name: addr?.name,
            state: addr?.state,
            street: addr?.street,
            zipCode: addr?.zipCode,
            country: addr?.country
          };
          arr.push({ label: labelSet, value: mainLoc.locationId, response: addressObj });
        }

        let subLocs = locations[i].subLocations;
        if (subLocs) {
          for (let j = 0; j < subLocs.length; j++) {
            let addr = subLocs[j]?.address;
            let stateVal = stateConvert(addr?.state) || addr?.state;
            let label = `${addr?.name}, ${addr?.street}, ${addr?.city}, ${stateVal}, ${addr?.zipCode}`;
            let addressObj = {
              city: addr?.city,
              contactNumber: addr?.contactNumber,
              locationName: addr?.locationName,
              name: addr?.name,
              state: addr?.state,
              street: addr?.street,
              zipCode: addr?.zipCode,
              country: addr?.country
            };
            arr.push({ label: label, value: subLocs[j]?.locationId, response: addressObj });
          }
        }
      }
      setLocationListResponse(arr);
    } else {
      setModalContent(response?.data?.error?.message);
      setModalLink("");
      toggleModal();
    }
  }, [loggedInUserInfo?.token, loggedInUserInfo?.locationType, loggedInUserInfo?.customerId, dispatch, locale]);

  // 2. Order Cloning & Editing Logic
  const populateFormFromOrderData = (orderData, setFieldValue) => {
    if (!orderData || !orderData.palletDetails?.material) {
      setModalContent("Invalid Order ID.");
      setModalLink("");
      toggleModal();
      return;
    }

    const pd = orderData.palletDetails;
    let materialVal = pd.material || "WOOD";
    setMaterialResponse(materialVal);
    if (setFieldValue) setFieldValue("material", materialVal);

    if (setFieldValue) {
      setFieldValue("condition0", false);
      setFieldValue("condition1", false);
      setFieldValue("condition2", false);
      setFieldValue("condition3", false);
    }

    if (pd.condition) {
      let conds = pd.condition;
      if (setFieldValue) {
        if (conds.includes("RECYCLED")) setFieldValue("condition0", true);
        if (conds.includes("NEW")) {
          setFieldValue("condition1", true);
          let newType = pd.newConditionType;
          let newConditionTypeVal = (newType === "HARDWOOD" || newType === "New - Hardwood") ? "New - Hardwood" : (newType === "SOFTWOOD" || newType === "New - Softwood") ? "New - Softwood" : newType || "";
          setFieldValue("hardOrSoftWood", newConditionTypeVal);
          setHardOrSoftWoodValue(newConditionTypeVal);
        }
        if (conds.includes("COMBO")) setFieldValue("condition2", true);
        if (conds.includes("CORES")) setFieldValue("condition3", true);
      }
    }

    if (orderData.equipmentRequirements && setFieldValue) {
      const eq = orderData.equipmentRequirements;
      setFieldValue("equipment0", eq.includes("FLAT_BED"));
      setFieldValue("equipment1", eq.includes("ENCLOSED_TRAILER"));
      setFieldValue("equipment2", eq.includes("STRAIGHT_TRUCK"));
    }

    let pType = pd.palletType?.toLowerCase();
    if (pType === "euro" || pType === "europallet") pType = "euroPallet";

    if (setFieldValue) {
      setFieldValue("palletType", pType || "");
      setFieldValue("moisture", pd.moisture || "");
      setFieldValue("size", pd.size || "");
      setFieldValue("grade", pd.grade || "");
    }

    if (orderData.specificationFiles && orderData.specificationFiles.length > 0) {
      const files = orderData.specificationFiles.map(f => ({
        orignalFileName: f.orignalFileName || f.fileName,
        fileName: f.fileName
      }));
      setUploadImage(files);
      setUploadSpectState(files);
    }

    if (pd.length && pd.width && setFieldValue) {
      setFieldValue("length", pd.length);
      setFieldValue("width", pd.width);
      setFieldValue("height", pd.height);
    }

    setIsHeatTreared(pd.condition?.[0] !== "CORES" && materialVal === "WOOD");
    if (setFieldValue) setFieldValue("isHeatTreared", pd.heatTreared !== undefined ? pd.heatTreared : (pd.heatTreated !== undefined ? pd.heatTreated : false));

    const clonedLocationId = orderData.location?.locationId || orderData.locationId || "";
    if (setFieldValue) {
      setFieldValue("businessName", clonedLocationId);
    }

    if (orderData.loadDetails) {
      let loadData = Array.isArray(orderData.loadDetails) ? orderData.loadDetails[0] : orderData.loadDetails;
      if (loadData && setFieldValue) {
        const clonedOrderDate = moment(loadData?.requestDeliveryDate);
        const today = moment().startOf("day");
        const validDate = clonedOrderDate.isAfter(today) ? clonedOrderDate : moment().add(1, "day");

        const pd = orderData?.palletDetails;
        let pType = pd?.palletType?.toLowerCase() || "";
        if (pType === "euro" || pd?.palletType === "EUROPALLET") pType = "euroPallet";
        const condType = Array.isArray(pd?.condition) ? pd.condition[0]?.toLowerCase() : (pd?.condition?.toLowerCase() || "recycled");
        const detailsList = palletDetailResponse?.[pd?.material || "WOOD"]?.condition?.[condType]?.[pType] || [];
        const matched = Array.isArray(detailsList) ? detailsList.find(d => d.size === pd?.size) : null;
        const defaultMaterialMax = pd?.material === "PLASTIC" ? 786 : 540;
        const originalQty = matched?.quantity || defaultMaterialMax;
        const clonedPo = loadData?.poNumber || orderData.poNumber || "";

        let loadDetailsObj = {
          lots: loadData?.quantityDetails?.lots || loadData?.lots || 1,
          QuantityLot: loadData?.quantityDetails?.quantity || loadData?.QuantityLot || 540,
          quantityMaxValue: originalQty,
          requestDeliveryDate: validDate.format("MM/DD/YYYY"),
          appt: loadData?.deliveryFromtime || loadData?.appt || "00:00",
          apptTo: loadData?.deliveryTotime || loadData?.apptTo || "23:00",
          poNumber: clonedPo,
        };

        if (setFieldValue) {
          setFieldValue("addMoreArrList", [loadDetailsObj]);
          setFieldValue("quantityMaxValue", originalQty);
          setFieldValue("poNumber", clonedPo);
        }
        setMaxQuantityState(originalQty);
        setLoadDetailsState([loadData]);
      }
    }

    const clonedRemark = orderData.remark || orderData.comments || orderData.remarkText || "";
    if (setFieldValue) {
      setFieldValue("comments", clonedRemark);
    }
  };

  const fetchOrderDetailsByRef = useCallback(async (refNumber, props, setFieldValue) => {
    let response = await dispatch(orderRefNumber(refNumber, loggedInUserInfo?.token));
    if (response?.data?.success && response?.data) {
      const orderData = response.data.data || response.data;
      const isMixOrder = Boolean(
        orderData?.mixPalletDetails === true ||
        (orderData?.palletCombinations && Array.isArray(orderData.palletCombinations) && orderData.palletCombinations.length > 0)
      );

      if (isModifyForm) {
        if (isMixOrder) {
          setEditResponse(response.data);
          setIsMixLoadMode(true);
          if (setFieldValue) {
            setFieldValue("cloneOrder", true);
            setFieldValue("orderId", refNumber);
          }
          return;
        }
      } else {
        if (isMixOrder) {
          setModalContent(locale('mixPalletCloneError') || "Mix pallet order cannot be cloned in single pallet page.");
          setModalTitle('');
          setModalLink("");
          toggleModal();
          return;
        }
      }

      setEditResponse(response.data);
      populateFormFromOrderData(orderData, setFieldValue);
      if (setFieldValue) {
        setFieldValue("cloneOrder", true);
        setFieldValue("orderId", refNumber);
      }

      if (orderData?.shippingAddress || orderData?.location) {
        let obj = {
          label: orderData?.shippingAddress?.name || orderData?.location?.locationName,
          value: orderData?.location?.locationId || orderData?.locationId,
          response: orderData?.shippingAddress,
        };
        setAddresResponse(obj);
      }

      if (orderData?.productImage && orderData.productImage.length > 0) {
        setDefaultPostImage({
          fileName: orderData.productImage[0]?.fileName,
          orignalFileName: orderData.productImage[0]?.orignalFileName,
        });
      }

      let pType = orderData?.palletDetails?.palletType?.toLowerCase();
      if (pType === "europallet" || pType === "euro") pType = "euroPallet";
      updatePalletCascadingOptions(props, pType, true, response?.data);
    } else {
      setModalContent(response?.data?.error?.message || response?.message || locale('genericErrorLater'));
      setModalLink("");
      toggleModal();
    }
  }, [dispatch, loggedInUserInfo?.token, locale]);

  const hasLoadedEditRef = useRef(null);

  // Sync maxQuantityState and hardOrSoftWoodValue on Edit Load
  useEffect(() => {
    if (editResponse) {
      const pd = editResponse?.data?.data?.palletDetails || editResponse?.data?.palletDetails || editResponse?.palletDetails;
      if (pd?.newConditionType) {
        const val = (pd.newConditionType === "HARDWOOD" || pd.newConditionType === "New - Hardwood")
          ? "New - Hardwood"
          : (pd.newConditionType === "SOFTWOOD" || pd.newConditionType === "New - Softwood")
            ? "New - Softwood"
            : pd.newConditionType;
        setHardOrSoftWoodValue(val);
      }

      let ld = editResponse?.data?.data?.loadDetails || editResponse?.data?.loadDetails || editResponse?.loadDetails;
      if (Array.isArray(ld)) ld = ld[0];
      let pType = pd?.palletType?.toLowerCase() || "";
      if (pType === "euro" || pd?.palletType === "EUROPALLET") pType = "euroPallet";
      const condType = Array.isArray(pd?.condition) ? pd.condition[0]?.toLowerCase() : (pd?.condition?.toLowerCase() || "recycled");
      const detailsList = palletDetailResponse?.[pd?.material || "WOOD"]?.condition?.[condType]?.[pType] || [];
      const matched = Array.isArray(detailsList) ? detailsList.find(d => d.size === pd?.size) : null;
      const defaultMaterialMax = pd?.material === "PLASTIC" ? 786 : 540;
      const maxQty = matched?.quantity || defaultMaterialMax;
      if (maxQty) setMaxQuantityState(maxQty);
    }
  }, [editResponse]);

  // Handle Edit Mode Initial Load
  useEffect(() => {
    if (
      isModifyForm &&
      modifiedOrderRefNo &&
      palletDetailResponse &&
      locationListResponse.length > 0 &&
      hasLoadedEditRef.current !== modifiedOrderRefNo
    ) {
      hasLoadedEditRef.current = modifiedOrderRefNo;
      fetchOrderDetailsByRef(modifiedOrderRefNo);
    }
  }, [isModifyForm, modifiedOrderRefNo, palletDetailResponse, locationListResponse, fetchOrderDetailsByRef]);

  // 3. Merged Submission Handler with Top-Level Validations
  const handleSubmitOrder = async (props) => {
    const values = props?.values || {};

    // --- Validation 1: Location ---
    if (!values.businessName) {
      setModalContent(locale('locationNameRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- Validation 2: Equipment Requirements ---
    const equipmentRequirements = [];
    if (values.equipment0) equipmentRequirements.push("FLAT_BED");
    if (values.equipment1) equipmentRequirements.push("ENCLOSED_TRAILER");
    if (values.equipment2) equipmentRequirements.push("STRAIGHT_TRUCK");

    if (equipmentRequirements.length === 0) {
      setModalContent(locale('equipmentRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- Validation 3: Material & Conditions ---
    let conditionArr = [];
    if (values.condition0) conditionArr.push("RECYCLED");
    if (values.condition1) conditionArr.push("NEW");
    if (values.condition2) conditionArr.push("COMBO");
    if (values.condition3) conditionArr = ["CORES"];

    if (conditionArr.length === 0 && values.material !== "OTHER") {
      setModalContent(locale('conditionRequired'));
      setModalLink("");
      toggleModal();
      return;
    }
    if (values.material === "WOOD" && !values.palletType) {
      setModalContent(locale('palletTypeRequired'));
      setModalLink("");
      toggleModal();
      return;
    }
    if (getMoisture.length > 0 && !values.moisture) {
      setModalContent(locale('moistureRequired'));
      setModalLink("");
      toggleModal();
      return;
    }
    if (values.material === "WOOD" && values.palletType && !values.size) {
      setModalContent(locale('sizeRequired'));
      setModalLink("");
      toggleModal();
      return;
    }
    if (getGradeList.length > 0 && !values.grade) {
      setModalContent(locale('gradeRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- Validation 4: Custom Dimensions & File Upload ---
    const isCustomDimensions = values.grade === "Custom" || values.size === "Custom" || values.material === "OTHER" || values.material === "PLASTIC";
    if (isCustomDimensions && (!values.length || !values.width || !values.height)) {
      setModalContent(locale('sizeRequired'));
      setModalLink("");
      toggleModal();
      return;
    }
    if (values.size === "Custom" && !pendingFiles && (!uploadSpectState || uploadSpectState.length === 0)) {
      setModalContent(locale('uploadSpecificationRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- Validation 5: Clone Reference ID ---
    if (values.cloneOrder && !values.orderId) {
      setModalContent(locale('orderIdRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- Validation 6: Appointment Times & Loads ---
    if (!values.addMoreArrList || values.addMoreArrList.length === 0) {
      setModalContent(locale('numberOfLoadsRequired'));
      setModalLink("");
      toggleModal();
      return;
    }

    for (let i = 0; i < values.addMoreArrList.length; i++) {
      const item = values.addMoreArrList[i];
      if (!item.lots) {
        setModalContent(locale('numberOfLoadsRequired'));
        setModalLink("");
        toggleModal();
        return;
      }
      if (!item.QuantityLot) {
        setModalContent(locale('quantityPerLoadRequired'));
        setModalLink("");
        toggleModal();
        return;
      }
      const fromTime = parseInt((item.appt || "00:00").replace(":", ""));
      const toTime = parseInt((item.apptTo || "23:00").replace(":", ""));
      if (toTime < fromTime) {
        setModalContent(locale('appointmentWindowInvalid'));
        setModalLink("");
        toggleModal();
        return;
      }
    }

    const minimalStartDate = moment(new Date().setDate(new Date().getDate() + 1)).format(FORMAT);
    const selectedDate = moment(values.addMoreArrList[0]?.requestDeliveryDate);
    if (selectedDate.isBefore(minimalStartDate)) {
      setModalContent(locale('deliveryDateBeforeMin'));
      setModalLink("");
      toggleModal();
      return;
    }

    // --- All Validations Passed ➔ Handle File Uploads & API Submission ---
    let finalSpectFiles = uploadSpectState || [];

    if (pendingFiles && pendingFiles.length > 0) {
      let formData = new FormData();
      for (let i = 0; i < pendingFiles.length; i++) {
        formData.append("file", pendingFiles[i]);
      }
      let uploadRes = await dispatch(uploadSpectFile(formData, loggedInUserInfo?.token));
      if (uploadRes?.data?.success && uploadRes?.data?.data?.s3UploadedFiles) {
        finalSpectFiles = uploadRes.data.data.s3UploadedFiles.map(f => ({
          orignalFileName: f.fileName,
          fileName: f.s3FfileName,
        }));
        setUploadSpectState(finalSpectFiles);
      } else {
        setModalContent(uploadRes?.data?.error?.message || uploadRes?.message || locale('genericErrorLater'));
        setModalLink("");
        toggleModal();
        return;
      }
    }

    const loadDetailsArr = values.addMoreArrList.map(item => ({
      requestDeliveryDate: moment(item.requestDeliveryDate).format("YYYY-MM-DD HH:mm"),
      deliveryFromtime: item.appt || "00:00",
      deliveryTotime: item.apptTo || values.apptTo || "23:00",
      lotType: values.lotsOption || "MULTIPLE",
      poNumber: item.poNumber || "",
      postType: "NEED",
      quantityDetails: {
        lots: parseInt(item.lots || 1),
        quantity: item.QuantityLot,
        originalQuantity: parseInt(maxQuantityState || values.quantityMaxValue || (values.material === "PLASTIC" ? 786 : 540)),
      },
    }));

    const activeLocationId = values.businessName || addresResponse?.value || "";
    const activeShippingAddress = locationListResponse?.find(l => l.value === activeLocationId)?.response || addresResponse?.response;

    const payload = {
      palletDetails: {
        material: values.material,
        condition: conditionArr,
        palletType: values.palletType ? values.palletType.toUpperCase() : 'CUSTOM',
        moisture: values.moisture || "",
        size: values.size || "",
        grade: values.grade || "",
        length: parseFloat(values.length) || "",
        width: parseFloat(values.width) || "",
        height: parseFloat(values.height) || "",
        heatTreared: values.isHeatTreared || false,
      },
      specificationFiles: finalSpectFiles,
      productImage: productPostImage ? [productPostImage] : null,
      orderRefNumber: isModifyForm ? modifiedOrderRefNo : "",
      equipmentRequirements,
      loadDetails: loadDetailsArr,
      location: {
        locationId: activeLocationId,
        shippingAddress: activeShippingAddress,
      },
      postRecurring: false,
      recurrance: null,
      remark: values.comments || "",
    };

    if (conditionArr.includes("NEW") && values.hardOrSoftWood) {
      payload.palletDetails.newConditionType = values.hardOrSoftWood === "New - Hardwood" ? "HARDWOOD" : "SOFTWOOD";
    }

    if (!isModifyForm) {
      payload.customer = {
        customerId: loggedInUserInfo?.customerId,
        locationType: loggedInUserInfo?.locationType,
      };
    } else {
      payload.modifiedBy = loggedInUserInfo?.name;
    }

    try {
      let response = isModifyForm
        ? await dispatch(updateOrder(payload, loggedInUserInfo?.token))
        : await dispatch(createOrder(payload, loggedInUserInfo?.token));

      if (response?.data?.success) {
        setModalTitle("");
        setModalContent(isModifyForm ? "Your order request has been updated!" : "Thank you! Your order request has been sent to a PalletTrader+ team member.");
        setModalLink("");
        toggleModal();
        setSuccessCreateOrder(true);
      } else {
        setModalContent(response?.data?.error?.message || response?.message || locale('genericErrorLater'));
        setModalLink("");
        toggleModal();
      }
    } catch (err) {
      setModalContent(locale('genericErrorLater'));
      setModalLink("");
      toggleModal();
    }
  };

  // 4. Cascading Specification Options Logic
  const updatePalletCascadingOptions = (props, palletType, editFlag, referenceResponse) => {
    let moistureArr = [];
    let conditionTypes = [];
    let selectedMaterial = (
      (editFlag && referenceResponse?.data?.palletDetails?.material) ||
      props?.values?.material ||
      "WOOD"
    ).toLowerCase();

    if (editFlag && referenceResponse?.data?.palletDetails?.condition?.length) {
      let conds = referenceResponse.data.palletDetails.condition;
      if (conds.includes("RECYCLED")) conditionTypes.push("recycled");
      if (conds.includes("NEW")) conditionTypes.push("newCondition");
      if (conds.includes("COMBO")) conditionTypes.push("combo");
      if (conds.includes("CORES")) conditionTypes.push("cores");
    } else {
      if (props?.values?.condition0) conditionTypes.push("recycled");
      if (props?.values?.condition1) conditionTypes.push("newCondition");
      if (props?.values?.condition2) conditionTypes.push("combo");
      if (props?.values?.condition3) conditionTypes.push("cores");
    }

    if (palletType === "custom") {
      setSizeList([{ label: "Custom", value: "Custom" }]);
    }
    if (palletType === "europallet" || palletType === "euro") {
      palletType = "euroPallet";
    }

    if (!palletDetailResponse || !palletDetailResponse[selectedMaterial]) return;

    for (let j = 0; j < conditionTypes.length; j++) {
      let response = palletDetailResponse[selectedMaterial]?.condition?.[conditionTypes[j]]?.[palletType];
      if (response?.palletSpecificDetails && response.palletSpecificDetails.length > 0) {
        for (let i = 0; i < response.palletSpecificDetails.length; i++) {
          if (response.palletSpecificDetails[i]?.moisture) {
            moistureArr.push({
              label: response.palletSpecificDetails[i].moisture,
              value: response.palletSpecificDetails[i].moisture,
            });
          }

          if (i === response.palletSpecificDetails.length - 1) {
            const moistureNames = moistureArr.map(val => val.value);
            const moistureListFilter = moistureArr.filter(({ value }, idx) => !moistureNames.includes(value, idx + 1));
            setMoisture(moistureListFilter);

            if (j === conditionTypes.length - 1) {
              if (editResponse || editFlag) {
                fetchSizeList(props, palletType, editFlag, referenceResponse);
              } else if (moistureArr.length === 0) {
                fetchSizeList(props, palletType);
              }
            }
          }
        }
      } else {
        setMoisture([]);
        setSizeList([]);
      }
    }
  };

  const fetchSizeList = (props, palletType, editFlag, referenceResponse) => {
    if (palletType === "europallet" || palletType === "euro") palletType = "euroPallet";

    let selectedMaterial = (
      (editFlag && referenceResponse?.data?.palletDetails?.material) ||
      props?.values?.material ||
      "WOOD"
    ).toLowerCase();

    let conditionTypes = [];
    if (editFlag && referenceResponse?.data?.palletDetails?.condition?.length) {
      let conds = referenceResponse.data.palletDetails.condition;
      if (conds.includes("RECYCLED")) conditionTypes.push("recycled");
      if (conds.includes("NEW")) conditionTypes.push("newCondition");
      if (conds.includes("COMBO")) conditionTypes.push("combo");
      if (conds.includes("CORES")) conditionTypes.push("cores");
    } else {
      if (props?.values?.condition0) conditionTypes.push("recycled");
      if (props?.values?.condition1) conditionTypes.push("newCondition");
      if (props?.values?.condition2) conditionTypes.push("combo");
      if (props?.values?.condition3) conditionTypes.push("cores");
    }

    if (!palletDetailResponse || !palletDetailResponse[selectedMaterial]) return;

    let sizeArr = [];
    for (let j = 0; j < conditionTypes.length; j++) {
      let response = palletDetailResponse[selectedMaterial]?.condition?.[conditionTypes[j]]?.[palletType];
      if (response?.palletSpecificDetails && response.palletSpecificDetails.length > 0) {
        for (let i = 0; i < response.palletSpecificDetails.length; i++) {
          if (response.palletSpecificDetails[i]?.size) {
            sizeArr.push({
              label: response.palletSpecificDetails[i].size,
              value: response.palletSpecificDetails[i].size,
              quantity: response.palletSpecificDetails[i].quantity,
            });
          }
          if (i === response.palletSpecificDetails.length - 1) {
            if (j === conditionTypes.length - 1) {
              if (referenceResponse?.data?.palletDetails?.grade || props?.values?.grade || sizeArr.length === 0) {
                fetchGradeList(props, palletType, editFlag, referenceResponse);
              }
            }
            const sizeNames = sizeArr.map(val => val.value);
            const sizeListFilter = sizeArr.filter(({ value }, idx) => !sizeNames.includes(value, idx + 1));
            setSizeList(sizeListFilter);
          }
        }
      }
    }
  };

  const fetchGradeList = (props, palletType, editFlag, referenceResponse) => {
    if (palletType === "europallet" || palletType === "euro") palletType = "euroPallet";

    let selectedMaterial = (
      (editFlag && referenceResponse?.data?.palletDetails?.material) ||
      props?.values?.material ||
      "WOOD"
    ).toLowerCase();

    let conditionTypes = [];
    if (editFlag && referenceResponse?.data?.palletDetails?.condition?.length) {
      let conds = referenceResponse.data.palletDetails.condition;
      if (conds.includes("RECYCLED")) conditionTypes.push("recycled");
      if (conds.includes("NEW")) conditionTypes.push("newCondition");
      if (conds.includes("COMBO")) conditionTypes.push("combo");
      if (conds.includes("CORES")) conditionTypes.push("cores");
    } else {
      if (props?.values?.condition0) conditionTypes.push("recycled");
      if (props?.values?.condition1) conditionTypes.push("newCondition");
      if (props?.values?.condition2) conditionTypes.push("combo");
      if (props?.values?.condition3) conditionTypes.push("cores");
    }

    if (!palletDetailResponse || !palletDetailResponse[selectedMaterial]) return;

    let gradeArr = [];
    for (let j = 0; j < conditionTypes.length; j++) {
      let response = palletDetailResponse[selectedMaterial]?.condition?.[conditionTypes[j]]?.[palletType];
      if (response?.palletSpecificDetails && response.palletSpecificDetails.length > 0) {
        for (let i = 0; i < response.palletSpecificDetails.length; i++) {
          if (response.palletSpecificDetails[i]?.grade) {
            gradeArr.push({
              label: response.palletSpecificDetails[i].grade,
              value: response.palletSpecificDetails[i].grade,
            });
          }
          if (i === response.palletSpecificDetails.length - 1) {
            const gradeNames = gradeArr.map(val => val.value);
            const gradeListFilter = gradeArr.filter(({ value }, idx) => !gradeNames.includes(value, idx + 1));
            setGradeList(gradeListFilter);
          }
        }
      } else {
        setMoisture([]);
        setSizeList([]);
      }
    }
  };

  // 5. JSX Sub-Component Renderers
  const renderMaterialSection = (setFieldValue) => (
    <div className="col-5 text-left responsive-mb-32">
      <p className="sec-label">{locale("material")}</p>
      <div className="row">
        {materialArr?.map((value, index) => (
          <div key={index} className="col-4">
            <Field
              component={RadioButton}
              className="responsive"
              name="material"
              id={value.value}
              defaultValue={materialResponse}
              onSelect={(e) => {
                setSizeList([]);
                setGradeList([]);
                setMoisture([]);
                setUploadImage();
                setDefaultPostImage();
                setFieldValue("palletType", "");
                setFieldValue("moisture", "");
                setFieldValue("size", "");
                setFieldValue("grade", "");
                setFieldValue("length", "");
                setFieldValue("width", "");
                setFieldValue("height", "");
                setIsHeatTreared(false);
                setFieldValue("isHeatTreared", false);
                setFieldValue("condition0", false);
                setFieldValue("condition1", false);
                setFieldValue("condition2", false);
                setFieldValue("condition3", false);
                setFieldValue("hardOrSoftWood", "");
                setHardOrSoftWoodValue("");
                if (e.target.value === "PLASTIC") {
                  setFieldValue("addMoreArrList.[0].QuantityLot", 786);
                  setFieldValue("quantityMaxValue", 786);
                  setMaxQuantityState(786);
                } else {
                  setFieldValue("addMoreArrList.[0].QuantityLot", 540);
                  setFieldValue("quantityMaxValue", 540);
                  setMaxQuantityState(540);
                }
                setMaterialResponse(e.target.value);
              }}
              label={value.label}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderConditionSection = (setFieldValue, arrList) => (
    <div className="text-left mb-1">
      <p className="sec-label">{locale("condition")}</p>
      <div className="row">
        {arrList?.map((value, index) => (
          <div style={{ width: "130px" }} key={index}>
            <CheckboxInput
              name={"condition" + index}
              label={arrList[index]}
              onChange={(val, label) => {
                setSizeList([]);
                setGradeList([]);
                setMoisture([]);
                setUploadImage();
                setDefaultPostImage();
                setFieldValue("palletType", "");
                setFieldValue("moisture", "");
                setFieldValue("size", "");
                setFieldValue("grade", "");
                setFieldValue("length", "");
                setFieldValue("width", "");
                setFieldValue("height", "");
                setIsHeatTreared(false);
                setFieldValue("isHeatTreared", false);
                setFieldValue("condition0", false);
                setFieldValue("condition1", false);
                setFieldValue("condition2", false);
                setFieldValue("condition3", false);
                setFieldValue("hardOrSoftWood", "");
                setHardOrSoftWoodValue("");

                if (label === 'Recycled' && val) setFieldValue("condition0", true);
                if (label === 'New' && val) setFieldValue("condition1", true);
                if (label === 'Combo' && val) setFieldValue("condition2", true);
                if (label === 'Cores' && val) setFieldValue("condition3", true);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderHardSoftWoodSection = (setFieldValue) => (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap' }}>
      {hardOrSoftWoodArray?.map((value, index) => (
        <HardSoftWoodWrapper key={index}>
          <Field
            component={RadioButton}
            className="responsive"
            name="hardOrSoftWood"
            id={value}
            defaultValue={hardOrSoftWoodValue}
            onSelect={(e) => {
              let tempValue = hardOrSoftWoodValue === e.target?.value ? "" : e.target?.value;
              setFieldValue("hardOrSoftWood", tempValue);
              setHardOrSoftWoodValue(tempValue);
            }}
            label={value}
          />
        </HardSoftWoodWrapper>
      ))}
    </div>
  );

  const renderPalletTypeSelect = (props, setFieldValue) => (
    <div className="app-input-text responsive-mb-26">
      <Select
        name="palletType"
        className="responsive"
        data={props?.values?.condition2 ? comboPalletList : props?.values?.condition3 ? corePalletList : palletList}
        placeholder={locale("palletType")}
        disabled={!props?.values?.condition0 && !props?.values?.condition1 && !props?.values?.condition2 && !props?.values?.condition3}
        searchable={true}
        onChangeHandler={(val) => {
          let pType = val;
          setDefaultPostImage();
          setUploadImage();
          setGradeList([]);
          setFieldValue("palletType", "");
          setFieldValue("moisture", "");
          setFieldValue("size", "");
          setFieldValue("grade", "");
          setFieldValue("length", "");
          setFieldValue("width", "");
          setFieldValue("height", "");
          setIsHeatTreared(false);
          setFieldValue("isHeatTreared", false);
          if (pType === "europallet") pType = "euroPallet";
          updatePalletCascadingOptions(props, pType);
        }}
      />
    </div>
  );

  const renderMoistureSelect = (props) => (
    <div className="app-input-text responsive-mb-26">
      <Select
        name="moisture"
        className="responsive"
        data={getMoisture}
        placeholder={locale("moisture")}
        disabled={getMoisture.length === 0}
        searchable={true}
        onChangeHandler={() => {
          setUploadImage();
          setDefaultPostImage();
          fetchSizeList(props, props?.values?.palletType);
        }}
      />
    </div>
  );

  const renderSizeSelect = (props, setFieldValue) => (
    <div className="app-input-text responsive-mb-26">
      <Select
        name="size"
        className="responsive"
        data={getSizeList}
        placeholder={locale("size")}
        disabled={getSizeList.length === 0}
        searchable={true}
        onChangeHandler={(val, data) => {
          setGradeList([]);
          setUploadImage();
          setDefaultPostImage();
          setFieldValue("grade", "");

          if (data?.quantity) {
            setMaxQuantityState(parseInt(data?.quantity));
            setQuantityValueState(data?.quantity);
            setFieldValue("addMoreArrList.[0].QuantityLot", data?.quantity);
            setFieldValue("quantityMaxValue", data?.quantity);

            let quantity = props?.values?.lots * data?.quantity;
            if (String(quantity).indexOf(".") !== -1) {
              quantity = parseInt(quantity) + 1;
            }
            if (quantity === 0) quantity = "";
            setFieldValue("TotalQuantity", quantity);
          }

          setIsHeatTreared(!props.values.condition3);

          let gradeArr = [];
          let filesArr = [];
          let conditionTypes = [];
          if (props?.values?.condition0) conditionTypes.push("recycled");
          if (props?.values?.condition1) conditionTypes.push("newCondition");
          if (props?.values?.condition2) conditionTypes.push("combo");
          if (props?.values?.condition3) conditionTypes.push("cores");

          const selectedMaterial = props?.values?.material.toLowerCase();
          let pType = props?.values?.palletType.toLowerCase();
          if (pType === "europallet") pType = "euroPallet";

          let response = palletDetailResponse[selectedMaterial]?.condition?.[conditionTypes[0]]?.[pType];
          for (let i = 0; i < response?.palletSpecificDetails.length; i++) {
            if (val === response?.palletSpecificDetails[i]?.size) {
              if (response?.palletSpecificDetails[i]?.grade !== "") {
                gradeArr.push({
                  label: response?.palletSpecificDetails[i]?.grade,
                  value: response?.palletSpecificDetails[i]?.grade,
                });
              }
              if (props?.values?.moisture === response?.palletSpecificDetails[i]?.moisture) {
                if (response?.palletSpecificDetails[i]?.file && response?.palletSpecificDetails[i]?.file !== "Custom Spec Upload") {
                  filesArr.push({
                    orignalFileName: response?.palletSpecificDetails[i]?.file,
                    fileName: response?.palletSpecificDetails[i]?.file,
                  });
                }
              } else if (!props?.values?.moisture && response?.palletSpecificDetails[i]?.file && response?.palletSpecificDetails[i]?.file !== "Custom Spec Upload") {
                filesArr.push({
                  orignalFileName: response?.palletSpecificDetails[i]?.file,
                  fileName: response?.palletSpecificDetails[i]?.file,
                });
              }

              if (response?.palletSpecificDetails[i]?.image) {
                setDefaultPostImage({
                  fileName: response?.palletSpecificDetails[i]?.image,
                  orignalFileName: response?.palletSpecificDetails[i]?.image,
                });
              }
            }

            if (i === response?.palletSpecificDetails.length - 1) {
              const gradeNames = gradeArr.map(val => val.value);
              const gradeListFilter = gradeArr.filter(({ value }, idx) => !gradeNames.includes(value, idx + 1));
              setGradeList(gradeListFilter);

              if (gradeListFilter.length === 0) {
                const filesNames = filesArr.map(val => val.fileName);
                const fileListFilter = filesArr.filter(({ fileName }, idx) => !filesNames.includes(fileName, idx + 1));
                setUploadImage(fileListFilter);
                setUploadSpectState(fileListFilter);
              }
            }
          }
        }}
      />
    </div>
  );

  const renderGradeSelect = (props) => (
    <div className="app-input-text responsive-mb-26">
      <Select
        name="grade"
        className="responsive"
        data={getGradeList}
        placeholder={locale("grade")}
        disabled={getGradeList.length === 0 || props?.values?.size === "Custom"}
        searchable={true}
        onChangeHandler={(val) => {
          setIsHeatTreared(!props.values.condition3);

          let filesArr = [];
          let conditionTypes = [];
          if (props?.values?.condition0) conditionTypes.push("recycled");
          if (props?.values?.condition1) conditionTypes.push("newCondition");
          if (props?.values?.condition2) conditionTypes.push("combo");
          if (props?.values?.condition3) conditionTypes.push("cores");

          const selectedMaterial = props?.values?.material.toLowerCase();
          let pType = props?.values?.palletType.toLowerCase();
          if (pType === "europallet") pType = "euroPallet";

          let response = palletDetailResponse[selectedMaterial]?.condition?.[conditionTypes[0]]?.[pType];
          for (let i = 0; i < response?.palletSpecificDetails.length; i++) {
            if (response?.palletSpecificDetails[i].size === props?.values?.size) {
              if (val === response?.palletSpecificDetails[i]?.grade) {
                if (response?.palletSpecificDetails[i]?.file && response?.palletSpecificDetails[i]?.file !== "Custom Spec Upload") {
                  filesArr.push({
                    orignalFileName: response?.palletSpecificDetails[i]?.file,
                    fileName: response?.palletSpecificDetails[i]?.file,
                  });
                }
                if (response?.palletSpecificDetails[i]?.image) {
                  setDefaultPostImage({
                    fileName: response?.palletSpecificDetails[i]?.image,
                    orignalFileName: response?.palletSpecificDetails[i]?.image,
                  });
                }
              }
            }

            if (i === response?.palletSpecificDetails.length - 1) {
              const filesNames = filesArr.map(val => val.fileName);
              const fileListFilter = filesArr.filter(({ fileName }, idx) => !filesNames.includes(fileName, idx + 1));
              setUploadImage(fileListFilter);
              setUploadSpectState(fileListFilter);
            }
          }
        }}
      />
    </div>
  );

  const renderCustomDimensionFields = () => (
    <div className="col-12 text-left">
      <div className="row">
        <div className="col-md-4 col-sm-6">
          <div className="app-input-text responsive-mb-26">
            <NumberInput
              name="length"
              className="responsive"
              placeholder={locale("length")}
              maxLength={5}
              showErrorMessages={true}
            />
          </div>
        </div>
        <div className="col-md-4 col-sm-6">
          <div className="app-input-text">
            <NumberInput
              name="width"
              className="responsive"
              placeholder={locale("width")}
              maxLength={5}
              showErrorMessages={true}
            />
          </div>
        </div>
        <div className="col-md-4 col-sm-6">
          <div>
            <NumberInput
              name="height"
              className="responsive"
              placeholder={locale("height")}
              maxLength={5}
              onChangeHandler={() => { }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderHeatTreatedCheckbox = () => (
    <div className="col-12 text-left mb-2">
      <div className="row" style={{ marginBottom: "10px" }}>
        <CheckboxInput
          name="isHeatTreared"
          label={locale("heatTreated")}
          handleSelect={() => { }}
        />
      </div>
    </div>
  );

  const renderSpecificationFileUpload = (props) => (
    <div className="stepUpload request-order-upload-section">
      <UploadSpecification
        acceptFiles="image/png,image/jpeg,image/gif"
        name="uploadSpecification"
        btnName={locale('uploadSpecification')}
        mandatory="Star"
        locale={locale}
        props={props}
        fileArr={[]}
        callBackFun={(imageArr) => {
          setUploadImage();
          setPendingFiles(imageArr);
        }}
      />
    </div>
  );

  const renderAttachedFilesBadge = () => (
    <div className="col-12 mb-2 max-3-row">
      {uploadImage?.map((item, index) => (
        <div className="row mb-1" key={index}>
          <UploadSpec className="upload col-12">
            <img src={UploadDummy} alt="upload Dummy" />
            <TooltipMaterial
              placement="top"
              arrow
              title={item?.fileName}
              enterTouchDelay={0}
            >
              <span
                onClick={() => {
                  const fileUrl = process.env.REACT_APP_API_URL + "/s3/get_file/" + item?.fileName;
                  window.open(fileUrl);
                }}
              >
                {item?.fileName}
              </span>
            </TooltipMaterial>
          </UploadSpec>
        </div>
      ))}
    </div>
  );

  // 6. Main Component View
  return (
    <BodyContainer>
      <Layout activeTab="RequestOrder">
        <OuterWrapper>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <TopHeading>
              <HeadingIcon />
              <div>
                <h4>{isModifyForm ? locale("updateOrderRequest") : locale("orderRequestForm")}</h4>
                <p>{locale("orderRequestIntro")}</p>
              </div>
            </TopHeading>
            {!isModifyForm && isUSA && (
              <AddMixPalletBtn
                type="button"
                active={isMixLoadMode}
                onClick={() => setIsMixLoadMode(!isMixLoadMode)}
                style={{ height: '40px' }}
              >
                {locale("addMixPallet")}
              </AddMixPalletBtn>
            )}
          </div>

          {isMixLoadMode ? (
            <MixPalletRequestOrder
              palletSpecsConfig={palletDetailResponse}
              onToggleMixLoad={() => setIsMixLoadMode(false)}
              isModifyForm={isModifyForm}
              modifiedOrderRefNo={modifiedOrderRefNo}
              editResponse={editResponse}
            />
          ) : (
            <Formik
              initialValues={getPostInitialValues(editResponse)}
              validationSchema={VALIDATION_SCHEMA()}
              enableReinitialize={true}
              onSubmit={() => { }}
            >
              {(props) => {
                const { isValid, dirty, setFieldValue } = props;

                return (
                  <Form className="form-wrapper">
                    <FormWrapper>
                      <div>
                        <div>
                          <div className="label-heading">
                            <p>
                              {locale("deliveryLocation")}
                              <span className="star">*</span>
                            </p>
                          </div>
                          {!isModifyForm && (
                            <div className="clone-outer-wrapper">
                              <div className="clone-order-wrapper">
                                <CheckboxInput
                                  className="responsive"
                                  name="cloneOrder"
                                  label={locale("clonePreviousOrder")}
                                  onChange={(val) => setCloneOrderFlag(val)}
                                />
                                <MaterialToolTip
                                  title={locale("cloneOrderTooltip")}
                                  class="passwordIcon"
                                  infoIcon={true}
                                />
                              </div>
                              {cloneOrderFlag && (
                                <div className="label-auto-adjust mb-20">
                                  <TextInput
                                    name="orderId"
                                    maxLength={50}
                                    placeholder={locale("enterReferenceId")}
                                    blurHandler={(val) => {
                                      if (val) fetchOrderDetailsByRef(val, props, setFieldValue);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          <div className="business-name-wrapper">
                            <div className="app-input-text mb-20">
                              <Select
                                name="businessName"
                                data={locationListResponse.length > 0 ? locationListResponse : [{ label: "", value: "" }]}
                                placeholder={locale("locationName")}
                                searchable={true}
                                onChangeHandler={(value, response) => setAddresResponse(response)}
                              />
                            </div>

                            <div className="equipment-requirements-wrapper mb-20">
                              <div className="label-heading">
                                <p>
                                  {locale("equipmentRequirements")}
                                  <span className="star">*</span>
                                </p>
                              </div>
                              <div className="requirements-checkbox">
                                {equipmentList?.map((value, index) => (
                                  <CheckboxInput
                                    name={"equipment" + index}
                                    label={equipmentList[index]}
                                    key={index}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="label-heading">
                              <p>
                                {locale("palletDetails")}
                                <span className="star">*</span>
                              </p>
                            </div>
                            <div className="col-12">
                              <div className="row">
                                {renderMaterialSection(setFieldValue)}
                                <div className="col-7 responsive-mb-32">
                                  {materialResponse === "WOOD" && renderConditionSection(setFieldValue, woodConditions)}
                                  {materialResponse === "PLASTIC" && renderConditionSection(setFieldValue, plasticConditions)}
                                  {props.values?.condition1 && materialResponse === "WOOD" && renderHardSoftWoodSection(setFieldValue)}
                                </div>
                              </div>
                            </div>

                            <div className="col-12">
                              <div className="row">
                                <div className="col-md-3">
                                  {materialResponse === "WOOD" && renderPalletTypeSelect(props, setFieldValue)}
                                </div>
                                <div className="col-md-3">
                                  {materialResponse === "WOOD" && renderMoistureSelect(props)}
                                </div>
                                <div className="col-md-3">
                                  {materialResponse === "WOOD" && renderSizeSelect(props, setFieldValue)}
                                </div>
                                <div className="col-md-3">
                                  {materialResponse === "WOOD" && renderGradeSelect(props)}
                                </div>
                              </div>
                            </div>

                            {(materialResponse !== "WOOD" || props?.values?.size === "Custom") && renderCustomDimensionFields()}
                            {isHeatTrearedFlag && renderHeatTreatedCheckbox()}
                            {renderSpecificationFileUpload(props)}
                            {uploadImage && renderAttachedFilesBadge()}

                            <LoadDetails
                              props={props}
                              isModifyForm={isModifyForm}
                              maxQuantityState={maxQuantityState}
                              editResponse={editResponse}
                              locale={locale}
                              callBack={(value) => setQuantityValueState(value)}
                              loadsCallBack={(data) => setLoadDetailsState(data)}
                            />
                          </div>

                          <div>
                            <div className="loan-detail-wrapper comment-section">
                              <div className="responsive-mb-32">
                                <div className="label-heading">
                                  <p>{locale("remarks")}</p>
                                </div>
                                <Textarea
                                  className="responsive"
                                  name="comments"
                                  inputType={"poNumber"}
                                  maxLength="800"
                                  showErrorMessages={true}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Footer>
                        <Button
                          type="app-green-button"
                          handleClick={() => handleSubmitOrder(props)}
                          disabled={!(isValid && dirty)}
                        >
                          {isModifyForm ? locale("updateRequest") : locale("submitRequest")}
                        </Button>
                      </Footer>
                    </FormWrapper>
                  </Form>
                );
              }}
            </Formik>
          )}

          <AlertModal
            isOpen={modal}
            toggle={toggleModal}
            title={modalTitle}
            content={modalContent}
            link={modalLink}
            className="alert-msg-container"
            InnerClassName="alert-msg"
            callBackAlert={() => {
              if (successCreateOrder) navigate("/manage-orders");
              else toggleModal();
            }}
          />
        </OuterWrapper>
      </Layout>
    </BodyContainer>
  );
};

export default RequestOrders;
